import { enhanceCsrfMultipartForms } from '../../shared/csrf';
import { enhanceGovernedHeaderMenus } from '../../shared/governedHeaderMenu';
import { initializeCompanyWorkspace } from './companyWorkspace';
import { initializeCompanyJobEditor } from './companyJobEditor';

const EMPLOYER_CAROUSEL_ENHANCED_ATTRIBUTE = 'data-fj-employer-carousel-enhanced';
const EMPLOYER_CAROUSEL_EDGE_TOLERANCE_PX = 2;
const PROFILE_STEPPER_ENHANCED_ATTRIBUTE = 'data-fj-profile-stepper-enhanced';

export function initializeEmployerCarousels(
    root: ParentNode = document,
    ownerWindow?: Window | null
): void {
    root.querySelectorAll<HTMLElement>('[data-fj-employer-carousel]').forEach((carousel) => {
        if (carousel.hasAttribute(EMPLOYER_CAROUSEL_ENHANCED_ATTRIBUTE)) return;
        const track = carousel.querySelector<HTMLElement>('[data-fj-employer-carousel-track]');
        const buttons = Array.from(carousel.querySelectorAll<HTMLButtonElement>('[data-fj-employer-carousel-step]'));
        if (!track || buttons.length === 0) return;
        const view = ownerWindow ?? carousel.ownerDocument.defaultView;
        if (!view) return;
        carousel.setAttribute(EMPLOYER_CAROUSEL_ENHANCED_ATTRIBUTE, 'true');

        const updateButtonState = (): void => {
            const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
            buttons.forEach((button) => {
                const direction = Number(button.dataset.fjEmployerCarouselStep) || 0;
                const disabled = direction < 0
                    ? track.scrollLeft <= EMPLOYER_CAROUSEL_EDGE_TOLERANCE_PX
                    : maxScroll - track.scrollLeft <= EMPLOYER_CAROUSEL_EDGE_TOLERANCE_PX;
                button.disabled = disabled;
                button.setAttribute('aria-disabled', String(disabled));
            });
        };

        const move = (button: HTMLButtonElement): void => {
            const direction = Number(button.dataset.fjEmployerCarouselStep) || 0;
            const card = track.querySelector<HTMLElement>('.fj-civic-employer-showcase-card');
            const trackStyle = view.getComputedStyle(track);
            const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap || '0');
            const distance = Math.max(track.clientWidth * 0.8, (card?.offsetWidth ?? track.clientWidth) + gap);
            track.scrollBy({ left: direction * distance, behavior: 'smooth' });
        };

        buttons.forEach((button) => {
            button.addEventListener('click', () => {
                move(button);
            });
        });

        track.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            const direction = event.key === 'ArrowLeft' ? -1 : 1;
            const button = buttons.find((candidate) => Number(candidate.dataset.fjEmployerCarouselStep) === direction);
            if (!button || button.disabled) return;
            event.preventDefault();
            move(button);
        });

        track.addEventListener('scroll', updateButtonState, { passive: true });
        view.addEventListener('resize', updateButtonState, { passive: true });
        updateButtonState();
    });
}

export function initializeCandidateProfileOnboarding(
    root: ParentNode = document,
    ownerWindow?: Window | null
): void {
    root.querySelectorAll<HTMLElement>('.fj-candidate-profile-entry__areas').forEach((stepper) => {
        if (stepper.hasAttribute(PROFILE_STEPPER_ENHANCED_ATTRIBUTE)) return;
        const view = ownerWindow ?? stepper.ownerDocument.defaultView;
        if (!view) return;
        stepper.setAttribute(PROFILE_STEPPER_ENHANCED_ATTRIBUTE, 'true');

        const focusTarget = (hash: string): void => {
            if (!/^#candidate-profile-[a-z]+$/.test(hash)) return;
            const target = stepper.ownerDocument.querySelector<HTMLElement>(hash);
            if (!target) return;
            view.requestAnimationFrame(() => target.focus({ preventScroll: true }));
        };

        stepper.querySelectorAll<HTMLAnchorElement>('a[href^="#candidate-profile-"]').forEach((link) => {
            link.addEventListener('click', () => focusTarget(link.hash));
        });
        view.addEventListener('hashchange', () => focusTarget(view.location.hash));
        if (view.location.hash) focusTarget(view.location.hash);
    });
}

document.addEventListener('DOMContentLoaded', () => {
    enhanceCsrfMultipartForms();
    enhanceGovernedHeaderMenus();
    initializeCompanyWorkspace();
    initializeCompanyJobEditor();
    initializeEmployerCarousels();
    initializeCandidateProfileOnboarding();
});
